import type { MgaCrewLine, MgaSettlementColumnDef } from './useData';

/** Short grid header labels for known settlement column codes. */
export const SETTLEMENT_HEADER_ABBR: Record<string, string> = {
  overtimeHours: 'OT Hrs',
  overtimeRate: 'OT Rate',
  previousBalance: 'Prev.',
  extraEarnings: 'Extra',
  cashAdvanceOffice: 'Office adv.',
  cashAdvanceMaster: 'Master adv.',
  cigarettesAmount: 'Cig.',
  internetCardsAmount: 'Internet',
  otherBondedAmount: 'Bonded',
  variousDeductions: 'Various',
};

export function settlementColumnHeader(col: MgaSettlementColumnDef): string {
  return SETTLEMENT_HEADER_ABBR[col.code] ?? col.name;
}

export function settlementColumnDefsForGrid(
  defs: MgaSettlementColumnDef[] | undefined,
): MgaSettlementColumnDef[] {
  const list = defs?.length
    ? defs.slice()
    : defaultSettlementColumnDefs();
  return list
    .filter((c) => c.showInGrid && c.isActive !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.code.localeCompare(b.code));
}

/** Fallback when API has not loaded settlementColumnDefs yet (legacy periods). */
function defaultSettlementColumnDefs(): MgaSettlementColumnDef[] {
  const manual = (
    code: string,
    name: string,
    sortOrder: number,
    columnType: 'EARNING' | 'DEDUCTION' = 'DEDUCTION',
  ) => ({
    id: 0,
    code,
    name,
    columnType,
    behavior: 'MANUAL' as const,
    includeInTotal: true,
    sortOrder,
    isActive: true,
    showInGrid: true,
  });
  const fromBooks = (code: string, name: string, sortOrder: number) => ({
    id: 0,
    code,
    name,
    columnType: 'DEDUCTION' as const,
    behavior: 'DERIVED' as const,
    sourceKey: 'REQUEST_LINES',
    includeInTotal: true,
    sortOrder,
    isActive: true,
    showInGrid: true,
  });
  return [
    { ...manual('overtimeHours', 'Overtime hours', 10, 'EARNING'), includeInTotal: false },
    { ...manual('overtimeRate', 'Overtime rate', 20, 'EARNING'), includeInTotal: false },
    manual('previousBalance', 'Previous balance', 30, 'EARNING'),
    manual('extraEarnings', 'Extra earnings', 40, 'EARNING'),
    fromBooks('cashAdvanceOffice', 'Cash advance (office)', 50),
    {
      id: 0,
      code: 'cashAdvanceMaster',
      name: 'Cash advance (master)',
      columnType: 'DEDUCTION',
      behavior: 'DERIVED',
      includeInTotal: true,
      sortOrder: 60,
      isActive: true,
      showInGrid: true,
    },
    fromBooks('cigarettesAmount', 'Cigarettes', 80),
    fromBooks('internetCardsAmount', 'Internet cards', 90),
    fromBooks('otherBondedAmount', 'Other bonded', 100),
    fromBooks('variousDeductions', 'Various deductions', 110),
  ];
}

export function settlementValueFromLine(line: MgaCrewLine, code: string): number {
  const fromAmount = line.settlementAmounts?.find((a) => a.columnCode === code);
  if (fromAmount) return Number(fromAmount.periodAmount) || 0;
  const legacy = (line as unknown as Record<string, unknown>)[code];
  return Number(legacy) || 0;
}

/** Columns that get an Allotment-style deduction list in the MGA Books tab. */
export function isDeductionListColumn(
  col: Pick<MgaSettlementColumnDef, 'code' | 'columnType' | 'sourceKey' | 'behavior'>,
): boolean {
  if (col.columnType !== 'DEDUCTION') return false;
  if (col.code === 'cashAdvanceMaster') return false;
  if (col.code === 'requestedAllotment') return false;
  if (col.code === 'allotmentAmount' || col.code === 'requestedCashByMaster') return true;
  return col.sourceKey === 'REQUEST_LINES';
}

/** Stock out → payroll charge categories. */
export const BONDED_PAYROLL_COLUMN_OPTIONS = [
  { value: 'cigarettesAmount', label: 'Cigarettes' },
  { value: 'internetCardsAmount', label: 'Internet cards' },
  { value: 'otherBondedAmount', label: 'Other bonded' },
] as const;

export function bondedPayrollColumnLabel(code: string | null | undefined): string {
  const hit = BONDED_PAYROLL_COLUMN_OPTIONS.find((o) => o.value === code);
  return hit?.label ?? code ?? '—';
}

/** True when the payroll grid may edit this settlement column (not managed in Books). */
export function isEditableSettlementColumn(col: MgaSettlementColumnDef): boolean {
  if (isDeductionListColumn(col)) return false;
  if (col.code === 'cashAdvanceMaster') return false;
  return col.behavior === 'MANUAL' || col.behavior === 'CARRY_FORWARD';
}
